// Client-side frequency cap for intent banners (first/exit).
//
// Two storage layers (see techspec "Cap de frequência"):
// - sessionStorage: 1x/session — once shown or dismissed in the session, do
//   not reopen for that intent type until a new session.
// - localStorage: 1x/day, cap of N views/campaign, 24h cooldown between views,
//   and a hard stop after the user clicks the CTA of a campaign.
//
// The per-campaign ledger is a JSON map keyed by campaign id:
//   { [campaignId]: { lastShownAt, dayKey, dayCount, totalCount, ctaClicked } }
import { getFromLocalStorage, setLocalStorage } from 'lib/localStorage';
import { getFromSessionStorage, setSessionStorage } from 'lib/sessionStorage';

import INTENT_BANNER from 'constants/intentBanner';
import LOCAL_STORAGE from 'constants/localStorage';
import SESSION_STORAGE from 'constants/sessionStorage';

const HOUR_MS = 60 * 60 * 1000;

const sessionKey = (type) => `${SESSION_STORAGE.INTENT_BANNER_SHOWN}_${type}`;

const todayKey = (now = Date.now()) => new Date(now).toISOString().slice(0, 10); // YYYY-MM-DD (UTC day bucket)

const readLedger = () => {
  try {
    return (
      JSON.parse(getFromLocalStorage(LOCAL_STORAGE.INTENT_BANNER_FREQUENCY)) ||
      {}
    );
  } catch {
    return {};
  }
};

const writeLedger = (ledger) => {
  try {
    setLocalStorage(
      LOCAL_STORAGE.INTENT_BANNER_FREQUENCY,
      JSON.stringify(ledger)
    );
  } catch {
    // Ignore storage errors (private mode / quota / disabled).
  }
};

const resolveFrequency = (frequency = {}) => ({
  ...INTENT_BANNER.DEFAULTS.FREQUENCY,
  ...frequency,
});

/**
 * Whether a campaign can be shown right now, honoring every cap layer.
 *
 * @param {object} params
 * @param {('first'|'exit')} params.type
 * @param {string|number} params.campaignId
 * @param {object} [params.frequency] - smartbanner_obj.intent.frequency
 * @param {number} [params.now] - injectable clock for tests
 */
export const canShowIntentBanner = ({
  type,
  campaignId,
  frequency,
  now = Date.now(),
}) => {
  // Session cap: shown/dismissed already this session FOR THIS CAMPAIGN. Keyed by
  // campaign id so that swapping the active first/exit banner (new id) resets the
  // session cap and the new campaign can show in the same session.
  const shownInSession = getFromSessionStorage(sessionKey(type));
  if (shownInSession && String(shownInSession) === String(campaignId)) {
    return false;
  }

  const cfg = resolveFrequency(frequency);
  const entry = readLedger()[String(campaignId)];

  if (!entry) {
    return true;
  }

  // Hard stop after CTA click.
  if (cfg.stopAfterCtaClick && entry.ctaClicked) {
    return false;
  }

  // Per-campaign total cap.
  if ((entry.totalCount || 0) >= cfg.perCampaign) {
    return false;
  }

  // Per-day cap.
  if (entry.dayKey === todayKey(now) && (entry.dayCount || 0) >= cfg.perDay) {
    return false;
  }

  // Cooldown between views.
  if (entry.lastShownAt && now - entry.lastShownAt < cfg.cooldownH * HOUR_MS) {
    return false;
  }

  return true;
};

/**
 * Record a view: bumps daily/total counters, stamps the time, and marks the
 * session so it won't reopen.
 */
export const markIntentBannerShown = ({
  type,
  campaignId,
  now = Date.now(),
}) => {
  setSessionStorage(sessionKey(type), String(campaignId));

  const ledger = readLedger();
  const id = String(campaignId);
  const entry = ledger[id] || {};
  const day = todayKey(now);

  ledger[id] = {
    ...entry,
    lastShownAt: now,
    dayKey: day,
    dayCount: entry.dayKey === day ? (entry.dayCount || 0) + 1 : 1,
    totalCount: (entry.totalCount || 0) + 1,
  };

  writeLedger(ledger);
};

/**
 * Record a dismiss: locks the session for this campaign (no reopen after
 * dismiss until a new session or a different campaign id).
 */
export const markIntentBannerDismissed = ({ type, campaignId }) => {
  setSessionStorage(sessionKey(type), String(campaignId));
};

/**
 * Record a CTA click: stops the campaign from showing again.
 */
export const markIntentBannerCtaClicked = ({ campaignId }) => {
  const ledger = readLedger();
  const id = String(campaignId);

  ledger[id] = {
    ...(ledger[id] || {}),
    ctaClicked: true,
  };

  writeLedger(ledger);
};
